// The steps the answering agent took, for support (Phase 5, 2026-09-26; §3 of
// `plans/phase-5-answering-design.md`, `document-ai/0021` §12.4; R118, R64).
//
// The dashboard's step display, rewritten here rather than imported: the two
// apps share contracts, not components (`architecture/0022`). Every line comes
// from `@gb/contracts` (`describeStep`, `summariseSteps`), written in the
// reader's locale; a title, a term and a note are shown as written. No code
// here reads a word: the icon comes from the `tool` field, the kind from the
// stored kind.
//
// **What only the backoffice shows, and shows whole** (§3, "Backoffice"):
// open by default; each row adds the input as JSON, the output whole in a
// scrolling panel with its size, a link to its run, the kept look it made or
// read, and a refusal's category. Row 0's output is block 2, whole. Model
// turns sit between tool rows, in the order they started. Nothing is cut: the
// first design's "N characters not shown" is gone (G25, §1.12). Code is
// folded after 12 lines, with one click to show the rest.

import { useState, type ReactNode } from "react";
import {
  Archive,
  Bot,
  ChevronRight,
  Code,
  Eye,
  FileText,
  Flag,
  GitBranch,
  Layers,
  Link2,
  List,
  Loader2,
  MessageSquare,
  OctagonAlert,
  Search,
  ShieldCheck,
  Table,
  Tags,
  Users,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router";
import type { AdminAnswerStep } from "@gb/admin-contracts";
import {
  StepDetailSchema,
  describeStep,
  summariseSteps,
  type AnswerStep,
  type AnswerTrail,
  type StepDetail,
} from "@gb/contracts";
import { cn } from "@/lib/utils";

/** A stored step carries what only support reads; a live one, from the stream, does not yet. */
export type ShownStep = AnswerStep & Partial<Omit<AdminAnswerStep, keyof AnswerStep>>;

/** Code is folded after this many lines; one click shows all of it (§1.12: a display fold, not a cap). */
export const CODE_FOLD_LINES = 12;

const TOOL_ICONS: Record<string, LucideIcon> = {
  search: Search,
  open_file: FileText,
  list_files: List,
  versions: GitBranch,
  links: Link2,
  metadata: Tags,
  crew: Users,
  kept_looks: Archive,
  look: Eye,
  rows_for_code: Table,
  code_execution: Code,
  findings: Flag,
};

const KIND_ICONS: Record<AnswerStep["kind"], LucideIcon> = {
  context: Layers,
  turn: Bot,
  tool: Wrench,
  note: MessageSquare,
  limit: OctagonAlert,
  "wrap-up": Flag,
  review: ShieldCheck,
};

function iconOf(step: AnswerStep): LucideIcon {
  if (step.kind === "tool") return (step.tool && TOOL_ICONS[step.tool]) || Wrench;
  return KIND_ICONS[step.kind];
}

/** The reader's locale: numbers and dates follow the reader, the words stay British English (§3). */
const LOCALE: string | undefined = undefined;

function num(n: number): string {
  return new Intl.NumberFormat(LOCALE).format(n);
}

/** "1.4 s", "3 min 12 s". */
export function took(ms: number): string {
  if (ms < 1_000) return `${num(ms)} ms`;
  const seconds = ms / 1_000;
  if (seconds < 60) return `${new Intl.NumberFormat(LOCALE, { maximumFractionDigits: 1 }).format(seconds)} s`;
  const whole = Math.round(seconds);
  return `${num(Math.floor(whole / 60))} min ${num(whole % 60)} s`;
}

function spanOf(step: AnswerStep): number | null {
  if (!step.finishedAt) return null;
  return Math.max(0, Date.parse(step.finishedAt) - Date.parse(step.startedAt));
}

/**
 * The header: tool calls, model turns and the time from the first step to
 * the last, then the guard reached when one was. Counts of the rows, so it
 * reads the same live and stored.
 */
export function headerLine(steps: readonly ShownStep[]): string {
  const calls = steps.filter((step) => step.kind === "tool").length;
  const turns = Math.max(0, ...steps.map((step) => step.turn));
  const starts = steps.map((step) => Date.parse(step.startedAt));
  const ends = steps.flatMap((step) => (step.finishedAt ? [Date.parse(step.finishedAt)] : []));
  const parts = [`${num(calls)} ${calls === 1 ? "tool call" : "tool calls"}`, `${num(turns)} ${turns === 1 ? "model turn" : "model turns"}`];
  if (starts.length > 0 && ends.length > 0) parts.push(took(Math.max(0, Math.max(...ends) - Math.min(...starts))));
  const limit = steps.find((step) => step.kind === "limit");
  if (limit) parts.push(describeStep(limit, LOCALE));
  return parts.join(" · ");
}

/** The collapsed line: the trail's summary when the answer is stored, else what is running now. */
function collapsedLine(steps: readonly ShownStep[], trail: AnswerTrail | null): string {
  if (trail) return summariseSteps(trail, LOCALE);
  const running = [...steps].reverse().find((step) => step.outcome === "running");
  const calls = steps.filter((step) => step.kind === "tool").length;
  if (running) return `${describeStep(running, LOCALE)} · ${num(calls)} ${calls === 1 ? "step" : "steps"}`;
  return steps.length === 0 ? "Thinking…" : `${num(calls)} ${calls === 1 ? "step" : "steps"}`;
}

export function AnswerSteps({
  steps,
  trail,
  tenantSlug,
}: {
  steps: readonly ShownStep[];
  /** The stored trail's counts, when the answer is stored. */
  trail: AnswerTrail | null;
  /** For the link to each step's run (`runs/:tenantSlug/:runId`). */
  tenantSlug: string | null;
}) {
  const [open, setOpen] = useState(true);
  return (
    <section aria-label="Steps" className="mt-3 rounded-lg border border-border bg-canvas">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left"
      >
        <ChevronRight size={14} className={cn("shrink-0 text-muted transition-transform", open && "rotate-90")} aria-hidden />
        <span aria-live="polite" className="min-w-0 flex-1 text-[0.8rem] font-semibold text-ink">
          {collapsedLine(steps, trail)}
        </span>
      </button>
      {open && (
        <div className="border-t border-border-inner px-3 py-2">
          <p className="mb-2 font-mono text-[0.72rem] text-faint">{headerLine(steps)}</p>
          {steps.length === 0 ? (
            <p className="text-[0.78rem] text-muted">No step was recorded. A scripted demo answer, or one from before the agent, has none.</p>
          ) : (
            <ol className="flex flex-col gap-1">
              {steps.map((step) => (
                <StepRow key={step.seq} step={step} tenantSlug={tenantSlug} />
              ))}
            </ol>
          )}
        </div>
      )}
    </section>
  );
}

const OUTCOME_TONES: Partial<Record<AnswerStep["outcome"], string>> = {
  error: "text-danger",
  timeout: "text-danger",
  "step-limit": "text-warn",
  repeat: "text-warn",
  cancelled: "text-warn",
};

/** One step: its line, opening to its detail. The thread page's whole list shows it too (`answer-made.tsx`). */
export function StepRow({ step, tenantSlug }: { step: ShownStep; tenantSlug: string | null }) {
  const [open, setOpen] = useState(false);
  const Icon = iconOf(step);
  const running = step.outcome === "running";
  const muted = step.kind === "note" || step.kind === "turn";
  return (
    <li className="text-[0.8rem]">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        className="flex w-full cursor-pointer items-start gap-2 rounded px-1 py-0.5 text-left hover:bg-white"
      >
        <span className="mt-0.5 w-5 shrink-0 text-right font-mono text-[0.68rem] text-faint">{step.seq}</span>
        {running ? (
          <Loader2 size={14} className="mt-0.5 shrink-0 animate-spin text-muted" aria-label="Running" />
        ) : (
          <Icon size={14} className="mt-0.5 shrink-0 text-muted" aria-hidden />
        )}
        <span className={cn("min-w-0 flex-1", muted ? "text-muted" : "text-ink", OUTCOME_TONES[step.outcome])}>
          {describeStep(step, LOCALE) || "(an empty note)"}
        </span>
        {step.outcome !== "ok" && !running && (
          <span className="shrink-0 font-mono text-[0.68rem] text-faint">{step.outcome}</span>
        )}
        <ChevronRight size={13} className={cn("mt-0.5 shrink-0 text-faint transition-transform", open && "rotate-90")} aria-hidden />
      </button>
      {open && <StepDetailView step={step} tenantSlug={tenantSlug} />}
    </li>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 text-muted">{label}</dt>
      <dd className="min-w-0 break-words text-ink">{children}</dd>
    </div>
  );
}

function StepDetailView({ step, tenantSlug }: { step: ShownStep; tenantSlug: string | null }) {
  const parsed = StepDetailSchema.safeParse(step.detail);
  const detail = parsed.success ? parsed.data : null;
  const span = spanOf(step);
  return (
    <div className="mt-1 mb-2 ml-8 flex flex-col gap-2 rounded border border-border-inner bg-white p-2 text-[0.76rem]">
      <dl className="flex flex-col gap-0.5">
        <Fact label="Time">
          {new Date(step.startedAt).toLocaleTimeString(LOCALE)}
          {span !== null ? ` · took ${took(span)}` : " · running"}
        </Fact>
        {step.purpose && <Fact label="Why">{step.purpose}</Fact>}
        {step.tool && <Fact label="Tool">{step.tool}</Fact>}
        <Fact label="Outcome">{step.outcome}</Fact>
        {step.failure && <Fact label="Failure">{step.failure}</Fact>}
        {step.repeatOf !== null && <Fact label="Repeats">step {step.repeatOf}</Fact>}
        {step.timeoutSeconds !== null && <Fact label="Timeout">{num(step.timeoutSeconds)} s</Fact>}
        {step.refusal && <Fact label="Refused">{step.refusal}</Fact>}
        {step.sources.length > 0 && <Fact label="Cited as">{step.sources.map((n) => `[${n}]`).join(" ")}</Fact>}
        {step.runId && (
          <Fact label="Run">
            {tenantSlug ? (
              <Link to={`/runs/${tenantSlug}/${step.runId}`} className="font-mono text-brand hover:underline">
                {step.runId}
              </Link>
            ) : (
              <span className="font-mono">{step.runId}</span>
            )}
          </Fact>
        )}
        {step.lookId && (
          <Fact label="Kept look">
            <span className="font-mono">{step.lookId}</span>
          </Fact>
        )}
      </dl>
      {step.note && (
        <div>
          <p className="font-semibold text-ink-slate">
            Note, from a {step.note.form === "text" ? "text block: it can cite" : "thinking block: it cannot cite"}
            {step.note.markersRemoved > 0 ? ` · ${num(step.note.markersRemoved)} markers removed` : ""}
            {step.note.shown ? "" : " · not shown to the reader"}
          </p>
          <p className="whitespace-pre-wrap text-ink">{step.note.text}</p>
        </div>
      )}
      {detail && <DetailOf detail={detail} />}
      {step.input !== undefined && step.input !== null && <Body label="Input, as the model sent it" text={JSON.stringify(step.input, null, 2)} />}
      {step.output !== undefined && step.output !== null && (
        <Body
          label={step.kind === "context" ? "Block 2, as the model was given it" : "Output, as the model received it"}
          text={step.output}
          size={step.outputChars ?? step.output.length}
        />
      )}
    </div>
  );
}

/** A body shown whole, in a panel that scrolls, with its size. */
export function Body({ label, text, size }: { label: string; text: string; size?: number }) {
  return (
    <div>
      <p className="font-semibold text-ink-slate">
        {label} <span className="font-normal text-muted">({num(size ?? text.length)} characters)</span>
      </p>
      <pre className="mt-0.5 max-h-[28rem] overflow-auto rounded bg-canvas p-2 font-mono text-[0.72rem] leading-[1.4] whitespace-pre-wrap text-ink">
        {text}
      </pre>
    </div>
  );
}

/** What each tool's detail adds, as the clients map §3.5 of the design lists it. */
function DetailOf({ detail }: { detail: StepDetail }) {
  switch (detail.tool) {
    case "search":
      return (
        <div>
          <p className="text-muted">
            Terms: {detail.terms.map((term) => `“${term}”`).join(", ")} · scope {detail.scope}
            {detail.scopeName ? ` “${detail.scopeName}”` : ""} · {num(detail.filesSearched)} files searched
            {detail.everyVisibleFile ? ", every visible file" : ""}
            {detail.unplaced > 0 ? ` · ${num(detail.unplaced)} hits with no snippet` : ""} · page {num(detail.page)} of {num(detail.pages)}
          </p>
          <ul className="ml-3 list-disc">
            {detail.results.map((result) => (
              <li key={result.documentId}>
                “{result.title}”: {num(result.hits)} {result.hits === 1 ? "hit" : "hits"}
              </li>
            ))}
          </ul>
        </div>
      );
    case "open_file":
      return (
        <div>
          {detail.passages.length > 0 && (
            <ul className="ml-3 list-disc">
              {detail.passages.map((passage, index) => (
                <li key={index}>
                  {passage.citation}
                  {passage.n !== null ? ` · cited as [${passage.n}]` : ""}
                </li>
              ))}
            </ul>
          )}
          {detail.notShown.length > 0 && <p className="text-muted">Not shown: {detail.notShown.join("; ")}</p>}
        </div>
      );
    case "look":
      return (
        <p className="text-muted">
          {detail.box
            ? `Region: from ${pct(detail.box.x)} across and ${pct(detail.box.y)} down, ${pct(detail.box.w)} wide and ${pct(detail.box.h)} high`
            : "The whole target"}
          {detail.width !== null && detail.height !== null ? ` · sent at ${num(detail.width)} × ${num(detail.height)} px` : ""}
          {detail.scale !== null && detail.scale !== 1 ? ` · scale ${detail.scale}` : ""}
          {detail.model ? ` · ${detail.model}` : ""}
          {detail.lookId ? " · kept" : " · not kept"}
          {detail.reason ? ` · why a new look: ${detail.reason}` : ""}
        </p>
      );
    case "kept_looks":
      return (
        <p className="text-muted">
          {num(detail.listed)} listed, {num(detail.opened)} opened
          {detail.lookIds.length > 0 ? `: ${detail.lookIds.join(", ")}` : ""}
        </p>
      );
    case "code_execution":
      return <FoldedCode code={detail.code} />;
    default:
      return null;
  }
}

function pct(share: number): string {
  return new Intl.NumberFormat(LOCALE, { style: "percent", maximumFractionDigits: 0 }).format(share);
}

/** Code, folded after `CODE_FOLD_LINES` lines, with one click to show all of it. */
export function FoldedCode({ code }: { code: string }) {
  const lines = code.split("\n");
  const [whole, setWhole] = useState(lines.length <= CODE_FOLD_LINES);
  return (
    <div>
      <p className="font-semibold text-ink-slate">Code</p>
      <pre className="mt-0.5 overflow-auto rounded bg-canvas p-2 font-mono text-[0.72rem] leading-[1.4] whitespace-pre text-ink">
        {whole ? code : lines.slice(0, CODE_FOLD_LINES).join("\n")}
      </pre>
      {!whole && (
        <button type="button" onClick={() => setWhole(true)} className="mt-0.5 text-[0.72rem] text-brand hover:underline">
          Show all {num(lines.length)} lines
        </button>
      )}
    </div>
  );
}
